/**
 * Training phase manager. Shows the active phase with its weekly_patterns
 * + week_assignment grid, recent adjustments, past phases, and a "New phase"
 * action.
 */

import { useState } from 'react';
import { Icon } from '@k-os/ui';
import {
  useTrainingPhases,
  useWorkouts,
  type TrainingPhaseDto,
  type WeeklyPattern,
  type WorkoutSessionDto,
} from '../../../api/health';
import { PhaseEditor } from './PhaseEditor';
import styles from './workouts.module.css';

export function PhaseManager({
  onOpenWorkout,
}: {
  onOpenWorkout?: (w: WorkoutSessionDto) => void;
} = {}) {
  const phasesQ = useTrainingPhases();
  const workoutsQ = useWorkouts();
  const [editing, setEditing] = useState<'new' | TrainingPhaseDto | null>(null);
  // When set, the page is replaced by a full-bleed detail view for the
  // chosen past phase. Same takeover pattern as WorkoutDetail and the
  // template preview, so the navigation feels consistent across the
  // Workouts area.
  const [viewingPhaseId, setViewingPhaseId] = useState<string | null>(null);

  if (phasesQ.isLoading) {
    return <div style={{ color: 'var(--ink-3)' }}>Loading phases…</div>;
  }
  const phases = phasesQ.data?.phases ?? [];
  const current = phases.find((p) => p.status === 'active') ?? null;
  const past = phases.filter((p) => p.status === 'completed');

  if (editing === 'new') {
    return <PhaseEditor onCancel={() => setEditing(null)} onSaved={() => setEditing(null)} />;
  }
  if (editing && typeof editing === 'object') {
    return (
      <PhaseEditor
        phase={editing}
        onCancel={() => setEditing(null)}
        onSaved={() => setEditing(null)}
        onDeleted={() => setEditing(null)}
      />
    );
  }

  // Past phase detail — read-only structure view with Edit + Back.
  if (viewingPhaseId) {
    const phase = phases.find((p) => p.id === viewingPhaseId);
    if (!phase) {
      // The phase has disappeared from under us (deleted in another tab,
      // for example). Fall back to the list rather than showing a blank.
      setViewingPhaseId(null);
      return null;
    }
    return (
      <PastPhaseDetail
        phase={phase}
        workouts={workoutsQ.data?.workouts ?? []}
        onBack={() => setViewingPhaseId(null)}
        onEdit={() => setEditing(phase)}
        {...(onOpenWorkout ? { onOpenWorkout } : {})}
      />
    );
  }

  return (
    <div>
      {current ? (
        <CurrentPhaseCard phase={current} onEdit={() => setEditing(current)} />
      ) : (
        <div
          style={{
            padding: 24,
            border: '1px dashed var(--line)',
            borderRadius: 10,
            background: 'var(--surface-2)',
            textAlign: 'center',
            color: 'var(--ink-3)',
            marginBottom: 18,
          }}
        >
          No active training phase. <button type="button" className="kos-btn kos-btn-primary" onClick={() => setEditing('new')} style={{ marginLeft: 8 }}>
            <Icon name="plus" size={11} /> Create phase
          </button>
        </div>
      )}

      {current && (
        <PhaseStructureView
          phase={current}
          workouts={workoutsQ.data?.workouts ?? []}
          {...(onOpenWorkout ? { onOpenWorkout } : {})}
        />
      )}

      <div style={{ marginTop: 32 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
            Past phases <span style={{ fontFeatureSettings: "'tnum'", color: 'var(--ink-4)', fontWeight: 400 }}>{past.length}</span>
          </h3>
          <button type="button" className="kos-btn kos-btn-primary" onClick={() => setEditing('new')}>
            <Icon name="plus" size={11} /> New phase
          </button>
        </div>
        {past.length === 0 ? (
          <div style={{ color: 'var(--ink-3)', fontSize: 14 }}>No past phases yet.</div>
        ) : (
          <div className={styles.libGrid}>
            {past.map((p) => (
              <div
                key={p.id}
                className={styles.libCard}
                role="button"
                tabIndex={0}
                onClick={() => setViewingPhaseId(p.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setViewingPhaseId(p.id);
                  }
                }}
                style={{ cursor: 'pointer' }}
                aria-label={`View ${p.name}`}
              >
                <div className={styles.libCardHead}>
                  <div className={styles.libCardName}>{p.name}</div>
                  <span className={styles.statusPill + ' ' + styles.statusDone}>Completed</span>
                </div>
                <div className={styles.libCardDesc}>{p.intent}</div>
                <div className={styles.libCardMeta}>
                  <span>
                    {p.startDate ?? '—'} → {p.endDate ?? '—'}
                  </span>
                  <span>{p.weeks ?? '—'} weeks</span>
                </div>
                {p.assessmentOutcome && (
                  <div
                    style={{
                      fontSize: 13,
                      color: 'var(--ink-3)',
                      fontStyle: 'italic',
                      borderLeft: '2px solid var(--accent)',
                      paddingLeft: 10,
                      marginTop: 4,
                    }}
                  >
                    {p.assessmentOutcome}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Past phase detail (full-page takeover) ─────────────────────────────

function PastPhaseDetail({
  phase,
  workouts,
  onBack,
  onEdit,
  onOpenWorkout,
}: {
  phase: TrainingPhaseDto;
  workouts: WorkoutSessionDto[];
  onBack: () => void;
  onEdit: () => void;
  onOpenWorkout?: (w: WorkoutSessionDto) => void;
}) {
  return (
    <div>
      <div className={styles.crumb}>
        <span
          className={styles.crumbLink}
          role="button"
          tabIndex={0}
          onClick={onBack}
          style={{ cursor: 'pointer' }}
        >
          Training phase
        </span>
        <Icon name="chevron" size={11} />
        <span className={styles.crumbActive}>{phase.name}</span>
      </div>

      <div className={styles.phaseHeader}>
        <div className={styles.phaseHeaderRow}>
          <span className={styles.statusPill + ' ' + styles.statusDone}>Completed</span>
          <h3 style={{ margin: 0, fontSize: 19, fontWeight: 600, letterSpacing: '-0.01em' }}>
            {phase.name}
          </h3>
          <span style={{ fontSize: 13, color: 'var(--ink-3)', background: 'var(--surface-2)', padding: '2px 7px', borderRadius: 4 }}>
            {phase.weeks ?? '—'} weeks
          </span>
          <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--ink-3)' }}>
            <span>
              {phase.startDate ?? '—'} → {phase.endDate ?? '—'}
            </span>
            <button type="button" className="kos-btn" onClick={onBack}>
              ← Back
            </button>
            <button type="button" className="kos-btn" onClick={onEdit}>
              <Icon name="settings" size={11} /> Edit phase
            </button>
          </span>
        </div>
        <div style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.55, marginBottom: 12, maxWidth: '65ch' }}>
          {phase.intent}
        </div>
        {(phase.primaryGoal || phase.secondaryGoal) && (
          <div className={styles.phaseGoals}>
            {phase.primaryGoal && (
              <span>
                <strong>Primary:</strong> {phase.primaryGoal}
              </span>
            )}
            {phase.secondaryGoal && (
              <span>
                <strong>Secondary:</strong> {phase.secondaryGoal}
              </span>
            )}
          </div>
        )}
        {phase.assessmentOutcome && (
          <div
            style={{
              marginTop: 14,
              paddingTop: 12,
              borderTop: '1px solid var(--line-2)',
              fontSize: 14,
              color: 'var(--ink-2)',
              lineHeight: 1.5,
            }}
          >
            <div
              style={{
                fontSize: 12,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'var(--ink-3)',
                marginBottom: 4,
              }}
            >
              Phase assessment
            </div>
            {phase.assessmentOutcome}
          </div>
        )}
      </div>

      <PhaseStructureView
        phase={phase}
        workouts={workouts}
        {...(onOpenWorkout ? { onOpenWorkout } : {})}
      />
    </div>
  );
}

// ─── Current phase card + week strip + day plan ─────────────────────────

function CurrentPhaseCard({
  phase,
  onEdit,
}: {
  phase: TrainingPhaseDto;
  onEdit: () => void;
}) {
  const progress = phase.weeks ? (phase.currentWeek / phase.weeks) * 100 : 0;
  return (
    <div className={styles.phaseHeader}>
      <div className={styles.phaseHeaderRow}>
        <span className={styles.phaseActiveBadge}>Active</span>
        <h3 style={{ margin: 0, fontSize: 19, fontWeight: 600, letterSpacing: '-0.01em' }}>
          {phase.name}
        </h3>
        <span style={{ fontSize: 13, color: 'var(--ink-3)', background: 'var(--surface-2)', padding: '2px 7px', borderRadius: 4 }}>
          Week {phase.currentWeek} / {phase.weeks ?? '—'}
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--ink-3)' }}>
          <span>
            {phase.startDate ?? '—'} → {phase.endDate ?? '—'}
          </span>
          <button type="button" className="kos-btn" onClick={onEdit}>
            <Icon name="settings" size={11} /> Edit phase
          </button>
        </span>
      </div>
      <div style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.55, marginBottom: 12, maxWidth: '65ch' }}>
        {phase.intent}
      </div>
      <div className={styles.phaseProgressBar}>
        <div className={styles.phaseProgressFill} style={{ width: `${Math.min(100, progress)}%` }} />
      </div>
      {(phase.primaryGoal || phase.secondaryGoal) && (
        <div className={styles.phaseGoals}>
          {phase.primaryGoal && (
            <span>
              <strong>Primary:</strong> {phase.primaryGoal}
            </span>
          )}
          {phase.secondaryGoal && (
            <span>
              <strong>Secondary:</strong> {phase.secondaryGoal}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function PhaseStructureView({
  phase,
  workouts,
  onOpenWorkout,
}: {
  phase: TrainingPhaseDto;
  workouts: WorkoutSessionDto[];
  onOpenWorkout?: (w: WorkoutSessionDto) => void;
}) {
  const patterns: WeeklyPattern[] =
    phase.weeklyPatterns.length > 0
      ? phase.weeklyPatterns
      : [
          {
            id: 'default',
            label: 'Weekly',
            days: [],
          },
        ];
  const assignment = phase.weekAssignment.length > 0
    ? phase.weekAssignment
    : Array(phase.weeks ?? 1).fill(patterns[0]!.id);
  const currentWeekIdx = phase.currentWeek - 1;
  const currentPatternId = assignment[currentWeekIdx] ?? patterns[0]!.id;
  const [selectedPatternId, setSelectedPatternId] = useState(currentPatternId);
  const selectedPattern =
    patterns.find((p) => p.id === selectedPatternId) ?? patterns[0]!;
  const multi = patterns.length > 1;

  return (
    <div className={styles.phaseGrid}>
      <div>
        {multi && (
          <>
            <div className={styles.phaseWeekStrip}>
              <span className={styles.phaseWeekStripLabel}>Week plan</span>
              <div className={styles.phaseWeekStripWeeks}>
                {assignment.map((pid, i) => {
                  const p = patterns.find((x) => x.id === pid) ?? patterns[0]!;
                  const isCurrent = i === currentWeekIdx;
                  const isSelected = pid === selectedPatternId;
                  return (
                    <button
                      key={i}
                      type="button"
                      className={`${styles.phaseWeekCell} ${isCurrent ? styles.phaseWeekCellCurrent : ''} ${isSelected ? styles.phaseWeekCellHighlighted : ''}`}
                      onClick={() => setSelectedPatternId(pid)}
                    >
                      <span className={styles.phaseWeekCellNum}>W{i + 1}</span>
                      <span className={styles.phaseWeekCellLabel}>{p.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={styles.subnav} style={{ marginBottom: 14 }}>
              {patterns.map((p) => {
                const usedIn = assignment.filter((pid) => pid === p.id).length;
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={`${styles.subnavItem} ${selectedPatternId === p.id ? styles.subnavActive : ''}`}
                    onClick={() => setSelectedPatternId(p.id)}
                  >
                    {p.label}
                    <span className={styles.subnavCount}>
                      {usedIn} wk{usedIn !== 1 ? 's' : ''}
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        <h3 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 600 }}>
          {multi ? `${selectedPattern.label} · daily plan` : 'Weekly structure'}{' '}
          <span style={{ fontFeatureSettings: "'tnum'", color: 'var(--ink-4)', fontWeight: 400 }}>
            {selectedPattern.days.length}
          </span>
        </h3>
        {selectedPattern.days.length === 0 ? (
          <div style={{ color: 'var(--ink-3)', fontSize: 14 }}>
            No days configured for this pattern yet.
          </div>
        ) : (
          selectedPattern.days.map((d, i) => {
            // Resolve this day to a concrete date inside the currently-
            // selected pattern week, then look for a matching workout
            // session by date. If found, the row becomes clickable and
            // opens the session in the tracker.
            const patternWeekIdx = phase.weekAssignment.findIndex((pid) => pid === selectedPattern.id);
            const showWeekIdx = patternWeekIdx >= 0 ? patternWeekIdx : currentWeekIdx;
            const concreteDate = phase.startDate
              ? resolveDate(phase.startDate, showWeekIdx, d.day)
              : null;
            const session = concreteDate
              ? workouts.find((w) => w.date === concreteDate && w.phaseId === phase.id)
              : undefined;
            const isRest = /^\s*rest\s*$/i.test(d.session);
            const clickable = !!session && !!onOpenWorkout && !isRest;
            return (
              <div
                key={i}
                className={styles.phaseDayRow}
                style={clickable ? { cursor: 'pointer' } : undefined}
                onClick={
                  clickable
                    ? () => session && onOpenWorkout && onOpenWorkout(session)
                    : undefined
                }
              >
                <span className={styles.phaseDayName}>{d.day}</span>
                <span className={styles.phaseDaySession}>
                  {d.session || (
                    <span style={{ color: 'var(--ink-4)', fontStyle: 'italic' }}>—</span>
                  )}
                </span>
                <span className={styles.phaseDayHint}>
                  {isRest
                    ? '—'
                    : session
                    ? sessionStatusLabel(session)
                    : 'Not yet materialized'}
                </span>
              </div>
            );
          })
        )}
      </div>

      <div>
        <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600 }}>
          Recent adjustments{' '}
          <span style={{ fontFeatureSettings: "'tnum'", color: 'var(--ink-4)', fontWeight: 400 }}>
            {phase.adjustments.length}
          </span>
        </h3>
        {phase.adjustments.length === 0 ? (
          <div style={{ color: 'var(--ink-4)', fontSize: 13, fontStyle: 'italic' }}>
            None yet. The Fitness Agent logs adjustments here as the phase progresses.
          </div>
        ) : (
          phase.adjustments.map((a, i) => (
            <div key={i} style={{ padding: '10px 0', borderBottom: '1px solid var(--line-2)' }}>
              <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>{a.date}</div>
              <div style={{ fontSize: 14, color: 'var(--ink-2)', marginTop: 2, lineHeight: 1.5 }}>
                {a.note}
              </div>
            </div>
          ))
        )}
        {phase.endDate && (
          <div style={{ marginTop: 18 }}>
            <h4 style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 600 }}>
              Phase assessment
            </h4>
            <div style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.5 }}>
              End-of-phase assessment scheduled for{' '}
              <strong style={{ color: 'var(--ink)' }}>{phase.endDate}</strong>.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── Date helpers for daily-plan ↔ workout-session lookup ────────────────

const DAY_LABEL_TO_DOW: Record<string, number> = {
  mon: 0, monday: 0,
  tue: 1, tuesday: 1,
  wed: 2, wednesday: 2,
  thu: 3, thursday: 3,
  fri: 4, friday: 4,
  sat: 5, saturday: 5,
  sun: 6, sunday: 6,
};

/**
 * Resolve a pattern-day label (Mon...Sun) inside week `weekIdx` of a phase
 * to a concrete YYYY-MM-DD date. Matches the backend's interpretation:
 * each phase week is the calendar week starting on the Monday of the week
 * containing the phase startDate.
 */
function resolveDate(startDateIso: string, weekIdx: number, dayLabel: string): string | null {
  const dow = DAY_LABEL_TO_DOW[dayLabel.trim().toLowerCase()];
  if (dow === undefined) return null;
  const start = new Date(startDateIso + 'T00:00:00Z');
  const jsDow = start.getUTCDay(); // 0 = Sunday
  const mondayOffset = jsDow === 0 ? -6 : 1 - jsDow;
  const monday = new Date(start.getTime());
  monday.setUTCDate(monday.getUTCDate() + mondayOffset);
  const target = new Date(monday.getTime());
  target.setUTCDate(target.getUTCDate() + weekIdx * 7 + dow);
  return target.toISOString().slice(0, 10);
}

function sessionStatusLabel(s: { status: string }): string {
  switch (s.status) {
    case 'completed': return 'Completed';
    case 'in_progress': return 'In progress';
    case 'skipped': return 'Skipped';
    case 'planned':
    default: return 'Planned';
  }
}
